import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowDown } from 'lucide-react';
import { hero } from '../mock';
import { useLang } from '../i18n';
import { useContent } from '../content';

const Hero = ({ onExplore, onQuote }) => {
  const { t } = useLang();
  const { stats } = useContent();
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setActive((p) => (p + 1) % hero.images.length);
    }, 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden bg-navy">
      {/* Background image layers */}
      {hero.images.map((img, i) => (
        <div
          key={img}
          className={`hero-bg-layer ${i === active ? 'active' : ''}`}
          style={{ backgroundImage: `url(${img})` }}
        />
      ))}
      {/* Overlays — warm orange gradient wash + readability darkening */}
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(118deg, rgba(214,88,20,0.62) 0%, rgba(239,123,36,0.30) 36%, rgba(7,26,46,0) 66%)' }}
      />
      <div
        className="absolute inset-0 mix-blend-soft-light"
        style={{ background: 'linear-gradient(118deg, rgba(255,150,60,0.9) 0%, rgba(255,150,60,0.25) 45%, rgba(0,0,0,0) 80%)' }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-navy/80 via-navy/25 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/15 to-navy/25" />
      {/* Dark mode: light wash so the hero reads darker but the image stays visible */}
      <div className="absolute inset-0 hidden dark:block bg-black/35" />

      {/* Content */}
      <div className="relative z-10 w-full">
        <div className="max-w-[1680px] mx-auto px-6 md:px-10 pt-40 pb-14">
          <h1 className="font-display font-light text-white leading-[1.12] tracking-tight max-w-[620px] text-5xl md:text-6xl lg:text-[74px]">
            {t('hero.lead')}
            <span className="text-ember">{t('hero.accent')}</span>
          </h1>
          <p className="mt-8 max-w-xl text-white/80 font-body text-base md:text-lg leading-relaxed">
            {t('hero.sub')}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="hero-quote-btn"
              onClick={onQuote}
              className="group inline-flex items-center justify-center gap-3 bg-ember hover:bg-ember-hover text-white px-8 py-4 label-mono transition-colors"
            >
              {t('hero.quote')}
              <ArrowRight size={16} strokeWidth={2} className="transition-transform group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-explore-btn"
              onClick={onExplore}
              className="group inline-flex items-center justify-center gap-3 border border-white/40 bg-white/5 backdrop-blur-sm text-white px-8 py-4 label-mono hover:bg-white/10 transition-colors"
            >
              {t('hero.explore')}
              <ArrowDown size={16} strokeWidth={2} className="transition-transform group-hover:translate-y-1" />
            </button>
          </div>
        </div>

        {/* Slide indicators */}
        <div className="absolute right-6 md:right-10 bottom-[220px] hidden md:flex flex-col gap-2 z-10">
          {hero.images.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`h-8 w-[2px] transition-all ${i === active ? 'bg-ember' : 'bg-white/30'}`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>

        {/* Stats bar */}
        <div className="relative border-t border-white/10 stats-band" style={{ background: 'linear-gradient(90deg, rgba(18,65,74,0.85) 0%, rgba(12,46,56,0.9) 100%)' }}>
          <div className="max-w-[1680px] mx-auto px-6 md:px-10">
            <div className="grid grid-cols-2 lg:grid-cols-4">
              {stats.map((s, i) => (
                <div
                  key={s.value}
                  className={`py-6 md:py-8 px-4 md:px-0 md:pr-6 flex flex-col gap-1.5 md:flex-row md:items-start md:gap-4 border-t border-white/10 ${i % 2 !== 0 ? 'border-l border-white/10' : ''} md:border-t-0 ${i !== 0 ? 'md:border-l md:border-white/10 md:pl-8' : ''}`}
                >
                  <span className="font-display font-extrabold text-ember text-3xl md:text-4xl leading-none">{s.value}</span>
                  <span className="font-mono-brand uppercase text-white/60 leading-relaxed text-[10px] md:text-[0.68rem] tracking-[0.1em] md:tracking-[0.2em] md:pt-1 break-words">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
